import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DATA_ADAPTER } from '../config/adapters';
import {
  isExchangeRatesStale,
  type DailyExchangeRates,
} from '../core/exchange/exchangeRates';
import { useAuth, useNotifs } from '../contexts/index';
import { usePresentAppError } from './usePresentAppError';

interface ExchangeRateRefreshResult {
  ok: boolean;
  refreshed?: boolean;
  keptPrevious?: boolean;
}

type PushFn = ReturnType<typeof useNotifs>['push'];

function isOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

function staleKey(rates: DailyExchangeRates | null | undefined): string {
  if (!rates) return 'missing';
  return `${rates.dayKey}|${rates.fetchedAt || rates.publishedAt || ''}`;
}

function notifyRefreshResult(
  result: ExchangeRateRefreshResult,
  silent: boolean,
  t: Record<string, string>,
  push: PushFn,
): boolean {
  if (!result?.ok) {
    if (!silent) {
      push({
        cat: 'system',
        sev: 'warn',
        title: t.exchangeRatesRefreshFailed,
        body: result?.keptPrevious ? t.exchangeRatesRefreshFailedHint : t.err_generic,
      });
    }
    return false;
  }
  if (!silent || result.keptPrevious) {
    push({
      cat: 'system',
      sev: result.keptPrevious ? 'warn' : 'success',
      title: result.keptPrevious
        ? t.exchangeRatesRefreshFailed
        : t.exchangeRatesRefreshSuccess,
      body: result.keptPrevious ? t.exchangeRatesRefreshFailedHint : '',
    });
  }
  return !result.keptPrevious;
}

export function useExchangeRateRefresh(
  t: Record<string, string>,
  rates?: DailyExchangeRates | null,
) {
  const { user } = useAuth();
  const { push } = useNotifs();
  const presentAppError = usePresentAppError();
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const autoSucceededKeyRef = useRef<string | null>(null);
  const autoInFlightRef = useRef(false);

  const canRefresh = useMemo(
    () => DATA_ADAPTER === 'firebase' && (user?.role === 'god' || user?.role === 'banker'),
    [user?.role],
  );

  useEffect(() => {
    refreshingRef.current = refreshing;
  }, [refreshing]);

  const runRefresh = useCallback(async (silent: boolean) => {
    if (!canRefresh || refreshingRef.current || !isOnline()) return false;
    if (!silent) {
      const confirmed = globalThis.confirm?.(t.exchangeRatesRefreshConfirm) ?? true;
      if (!confirmed) return false;
    }

    setRefreshing(true);
    try {
      const { refreshExchangeRates } = await import('../services/firebase/FunctionsService');
      const result = await refreshExchangeRates() as ExchangeRateRefreshResult;
      return notifyRefreshResult(result, silent, t, push);
    } catch (error) {
      if (!silent) {
        presentAppError(error, {
          t,
          push,
          context: { feature: 'exchangeRates', action: 'refresh' },
        });
      }
      return false;
    } finally {
      setRefreshing(false);
    }
  }, [canRefresh, presentAppError, push, t]);

  useEffect(() => {
    if (!canRefresh || !isExchangeRatesStale(rates ?? null)) return undefined;
    const key = staleKey(rates);
    if (autoSucceededKeyRef.current === key) return undefined;

    const tryAuto = () => {
      if (!isOnline() || !isExchangeRatesStale(rates ?? null)) return;
      if (autoSucceededKeyRef.current === key || autoInFlightRef.current) return;
      autoInFlightRef.current = true;
      void runRefresh(true).then((ok) => {
        if (ok) autoSucceededKeyRef.current = key;
      }).finally(() => {
        autoInFlightRef.current = false;
      });
    };

    tryAuto();
    window.addEventListener('online', tryAuto);
    return () => window.removeEventListener('online', tryAuto);
  }, [canRefresh, rates, runRefresh]);

  const refresh = useCallback(() => runRefresh(false), [runRefresh]);
  return { canRefresh, refreshing, refresh };
}
